/**
 * Storage Connections API endpoints for managing storage backends.
 */

import { api } from './client';
import type { ApiResponse } from '../types';

/** Storage type options */
export type StorageType = 'local' | 's3' | 'supabase';

/** S3-compatible storage provider */
export type S3Provider =
  | 'aws'
  | 'cloudflare_r2'
  | 'digitalocean'
  | 'backblaze'
  | 'wasabi'
  | 'minio'
  | 'custom';

/** S3 provider display names */
export const S3_PROVIDER_NAMES: Record<S3Provider, string> = {
  aws: 'AWS S3',
  cloudflare_r2: 'Cloudflare R2',
  digitalocean: 'DigitalOcean Spaces',
  backblaze: 'Backblaze B2',
  wasabi: 'Wasabi',
  minio: 'MinIO',
  custom: 'Custom S3-Compatible',
};

/** Storage connection data */
export interface StorageConnection {
  id: number;
  name: string;
  storage_type: StorageType;
  config_preview: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

/** Request body for creating a storage connection */
export interface CreateStorageConnectionRequest {
  name: string;
  storage_type: StorageType;
  // S3 fields
  bucket?: string;
  region?: string;
  access_key_id?: string;
  secret_access_key?: string;
  endpoint?: string;
  /** S3 provider (aws, cloudflare_r2, digitalocean, backblaze, wasabi, minio, custom) */
  provider?: S3Provider;
  /** Account ID (required for Cloudflare R2) */
  account_id?: string;
  /** Public URL base for R2 (R2.dev URL or custom domain) */
  public_url_base?: string;
  // Supabase fields
  supabase_url?: string;
  anon_key?: string;
}

/** Response for test connection endpoint */
export interface TestStorageConnectionResponse {
  success: boolean;
  healthy: boolean;
  message: string;
}

/** Storage Connections API */
export const storageConnectionsApi = {
  /** List all storage connections */
  list: () => api.get<ApiResponse<StorageConnection[]>>('/v1/storage-connections'),

  /** Get a specific storage connection */
  get: (id: number) =>
    api.get<ApiResponse<StorageConnection>>(`/v1/storage-connections/${id}`),

  /** Create a new storage connection */
  create: (data: CreateStorageConnectionRequest) =>
    api.post<ApiResponse<StorageConnection>>('/v1/storage-connections', data),

  /** Delete a storage connection */
  delete: (id: number) =>
    api.delete<ApiResponse<null>>(`/v1/storage-connections/${id}`),

  /** Test a storage connection by ID */
  test: (id: number) =>
    api.post<ApiResponse<TestStorageConnectionResponse>>(
      `/v1/storage-connections/${id}/test`,
      {}
    ),

  /** Test connection details without saving (inline test) */
  testInline: (data: CreateStorageConnectionRequest) =>
    api.post<ApiResponse<TestStorageConnectionResponse>>(
      '/v1/storage-connections/test-inline',
      data
    ),
};

/** Site Storage API */
export const siteStorageApi = {
  /** Update a site's storage connection */
  updateSiteStorage: (siteSlug: string, storageConnectionId: number | null) =>
    api.put<ApiResponse<null>>(`/v1/sites/${siteSlug}/storage-connection`, {
      storage_connection_id: storageConnectionId,
    }),
};
